import React, { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * One report on the Reports page, closed until asked for.
 *
 * Reports are wide and each one costs a query, so the page opens as a list of
 * what is available rather than everything at once. The body is mounted only
 * once opened — a closed report fetches nothing.
 */
const ReportCard: React.FC<{
  title: string;
  description: string;
  /** Open on first render. The only report on the page may as well be. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, description, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardActionArea
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        sx={{ p: { xs: 2, sm: 2.5 } }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              color: "text.secondary",
              transition: "transform 150ms",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        </Stack>
      </CardActionArea>

      {/* unmountOnExit: a closed report costs nothing, including its fetch. */}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 } }}>{children}</Box>
      </Collapse>
    </Card>
  );
};

export default ReportCard;
